import type { Tranche } from "@lib/tranche";
import type { Track } from "@lib/types";

import type { Aggregate } from "@engine/core";
import { z } from "zod";
import type { NotesAndFeedbackVisibility, RecusalVote } from "./actions";

export type Disclosure = {
  disclosure: string;
  selfRecused: boolean;
  votes: { [memberId: string]: RecusalVote };
  decision: "recused" | "not-recused" | null;
  hasDisclosure: boolean;
};

export const trackStatusSchema = z.enum([
  "1.onboarding", // only show recusals/investigation interest
  "2.initial-evaluations", // recommenders can only see proposals and their own MVF & notes, not others'
  "3.full-discussion", // see alluvial chart, disagreements, others' MVFs; everything
  "4.evals-freeze", // recommenders can see everything still but can't change MVFs
  "5.complete", // everything is in read-only mode
]);

export type TrackStatus = z.infer<typeof trackStatusSchema>;

type TrackSettings = {
  status: TrackStatus;
  evaluationsDueDate: string | undefined;
};

export interface RoundAggregate extends Aggregate {
  type: "Round";
  status: "active" | "deleted";
  name: string;
  holdProposalId: string;
  members: { [userId: string]: RoundMember };
  proposalForms: { [proposalFormId: string]: ProposalForm };
  investigation: {
    [proposalIdAndMemberId: string]: {
      interest: number | undefined;
      confirmedReviewer: boolean;
    };
  };
  reviewProgress: {
    [proposalIdAndMemberId: string]: number;
  };
  proposalDisclosure: {
    [proposalIdAndMemberId: string]: Disclosure;
  };
  trackSettings: {
    MAIN: TrackSettings;
    FAIRNESS: TrackSettings;
    FREEDOM: TrackSettings;
  };
}

export type Recommender = {
  id: string;
  memberId: string;

  type: "recommender";
  status: "active" | "removed";

  shortname: string;
  evaluations: { [evaluableId: string]: Evaluation };
  trancheList: Tranche[];

  // The number of evaluations (by humans) an evaluable must have for this mean
  // recommender to incorporate it. Set iff this is a mean recommender.
  meanRecommenderThreshold?: number;

  // How many internal dollars is an external dollar worth?
  outsideDollarSubstitutionRate: number;
};

export type Funder = {
  id: string;
  type: "funder";
  status: "active" | "removed";
  memberId: string;
  shortname: string;
  evaluations: { [evaluableId: string]: Evaluation };
  budget: number;
  speculationGrantSettlementBudget: number | null;
  trancheList: Tranche[];
  scores: {
    [recommenderId: string]: {
      [proposalId: string]: number | undefined;
    };
  };
  incrementalEvalSteps: {
    evaluableId: string;
    incrementAmount: number;
  }[];
  incrementalEvalIndex: number;
};

export type Node = Recommender | Funder | Proposal;

export type RoundMember = {
  status: "active" | "removed";

  // Note that userId and id are always the same! They're also the same as the
  // key used to look up this member in the round aggregate's members object.
  userId: string;
  id: string;

  isRoundAdmin: boolean;
  initials: string;
  track: Track;
  createdAt: string;
  feedbackVisibleByDefault: boolean;

  evaluableFeedback: {
    [evaluableId: string]: {
      positiveNegative: PositiveNegativeFeedback;
    };
  };
  evaluableNotes: { [evaluableId: string]: string };

  // Sharing/permissions for notes and feedback (evaluableFeedback and
  // evaluableNotes). Keys of this object are NOT the full set of evaluables;
  // if an evaluable is not included in this object, the permissions are
  // default (not shared).
  evaluablePermissions: {
    [evaluableId: string]: NotesAndFeedbackVisibility;
  };

  evaluators: { [evaluatorId: string]: Recommender | Funder };
};

export type EvaluationState =
  | "not-started"
  | "evaluated"
  | "canceled"
  | "hidden"
  | "recused"
  | "deferred";

export type Evaluation = {
  state: EvaluationState;
  deferredTo?: string;

  evaluableType: EvaluableType;
  evaluableId: string;

  // True if the recommender clicked "Set to 0" for this evaluation.
  setToZero?: boolean;

  expectedMatchingFraction?: number;
};

export type PositiveNegativeFeedback = z.infer<
  typeof evaluationFeedbackPositiveNegativeSchema
>;

export const evaluationFeedbackPositiveNegativeSchema = z.object({
  type: z.literal("positiveNegative"),
  positive: z.string(),
  negative: z.string(),
});

export const evaluationFeedbackTypeSchema =
  evaluationFeedbackPositiveNegativeSchema;

export type EvaluableFeedback = z.infer<typeof evaluationFeedbackTypeSchema>;

export type RoundMemberRecusal = {
  evaluableType: EvaluableType;
  evaluableId: string;
};

export const evaulationItemTypeSchema = z.enum(["proposal", "recommender"]);
export type EvaluableType = z.infer<typeof evaulationItemTypeSchema>;

export const evaluatorTypeSchema = z.enum(["recommender", "funder"]);

export interface ProposalForm {
  status: "active" | "deleted";
  id: string;
  name: string;
  googleFormId: string;
  spreadsheetId: string;
  spreadsheetRange: string;
  fields: Field[];
  proposals: { [proposalId: string]: Proposal };
}

export const fieldSourceSchema = z.enum([
  "longform",
  "googleform",
  "appInterface",
]);

export const fieldStatusSchema = z.enum(["active", "deleted"]);

const textQuestionSchema = z.object({
  status: fieldStatusSchema,
  type: z.literal("question"),
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  source: fieldSourceSchema,
});

const sectionHeaderSchema = z.object({
  status: fieldStatusSchema,
  type: z.literal("sectionHeader"),
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  source: fieldSourceSchema,
});

export const fieldSchema = z.discriminatedUnion("type", [
  textQuestionSchema,
  sectionHeaderSchema,
]);

const textResponseSchema = z.object({
  type: z.literal("question"),
  fieldId: z.string(),
  value: z.string(),
});

const errorResponseSchema = z.object({
  type: z.literal("error"),
  fieldId: z.string(),
  error: z.string(),
});

export const fieldResponseSchema = z.union([
  textResponseSchema,
  errorResponseSchema,
]);

const typedFieldsSchema = z.object({
  // Amount the S-process provides for every external dollar.
  matchRate: z.number().optional(),

  // The size of the standard grant which happens *before* any matching starts.
  baselineStandardGrantAmount: z.number().optional(),

  // The target amount of outside donations they'd like to have matched.
  // This, together with matchRate, decide the maximum matching pledge amount.
  targetOutsideDonations: z.number().optional(),

  baseRequestAmount: z.number(),
  minGrantAmount: z.number().optional(),
  maxGrantAmount: z.number().optional(),
  receivingOrgName: z.string(),
  receivingOrgTaxId: z.string(),
  receivingOrgLocation: z.string(),
  sffApplicationId: z.string(),
  longformLink: z.string(),

  targetsFreedom: z.boolean(),
  targetsFairness: z.boolean(),
});

export const entityType = z.enum([
  "non-profit",
  "for-profit",
  "social-welfare",
]);

export const proposalSchema = z.object({
  id: z.string(),
  type: z.literal("proposal"),
  status: z.enum(["active", "withdrawn"]),
  entityType,
  // (i.e. precise timestamp of Google Form submission)
  externalId: z.string().optional(),
  shortname: z.string(),
  longname: z.string(),
  firstSubmittedDatetime: z.string(),
  lastSubmittedDatetime: z.string(),
  requestsMatchingPledge: z.boolean(),
  typedFields: typedFieldsSchema,
  fieldResponses: z.record(z.string(), fieldResponseSchema),
});

export type TextQuestion = z.infer<typeof textQuestionSchema>;
export type SectionHeader = z.infer<typeof sectionHeaderSchema>;
export type FieldResponse = z.infer<typeof fieldResponseSchema>;
export type Proposal = z.infer<typeof proposalSchema>;
export type Field = z.infer<typeof fieldSchema>;
export type TypedFields = z.infer<typeof typedFieldsSchema>;
export type EntityType = z.infer<typeof entityType>;

export function matchLimit(proposal: Proposal): number | undefined {
  const fields = proposal.typedFields;
  if (!fields.matchRate || !fields.targetOutsideDonations) {
    return undefined;
  }
  return fields.targetOutsideDonations * fields.matchRate;
}
